import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import Icon from 'react-native-vector-icons/Ionicons';
import Clipboard from '@react-native-clipboard/clipboard';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { CopyableInfoRow } from '@/components/residents/mobile/CopyableInfoRow';
import { GlassCard } from '../GlassCard';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { WifiInfo } from '@/types/residents/wifi';
import { showAlert } from '@/utils/common/alert';

interface WifiInfoGlassCardProps {
  wifiInfo: WifiInfo;
  propertyName?: string;
  leaseUnit?: string;
  onPressQRCode?: () => void;
}

export const WifiInfoGlassCard: React.FC<WifiInfoGlassCardProps> = ({
  wifiInfo,
  propertyName,
  leaseUnit,
  onPressQRCode,
}) => {
  const { colors } = useThemeColors();

  const subtitle = [propertyName, leaseUnit].filter(Boolean).join(' · ');

  const handleCopy = (text: string, label: string) => {
    Clipboard.setString(text);
    showAlert('Copied', `${label} copied to clipboard`);
  };

  return (
    <GlassCard style={styles.card}>
      {onPressQRCode && (
        <Hoverable hover="button" style={styles.qrButton} onPress={onPressQRCode}>
          <Icon name="qr-code" size={18} color="#fff" />
        </Hoverable>
      )}

      <View style={[styles.cbxBadge, { backgroundColor: colors.accent.blueBgLight }]}>
        <RESIDENTS_SVG_COMPONENTS.CBX_LOGO width={64} height={20} />
      </View>

      {!!subtitle && (
        <View style={styles.subtitleRow}>
          <RESIDENTS_SVG_COMPONENTS.WIFI width={14} height={14} color={colors.text.muted} />
          <Text style={[styles.subtitle, { color: colors.text.muted }]}>{subtitle}</Text>
        </View>
      )}

      <View style={styles.credentialsSection}>
        <CopyableInfoRow
          label="NETWORK NAME"
          value={wifiInfo.ssid}
          onCopy={() => handleCopy(wifiInfo.ssid, 'Network name')}
          labelColor={colors.text.subtle}
          valueColor={colors.text.primary}
          iconColor={colors.text.subtle}
        />
      </View>

      <View style={styles.credentialsSection}>
        <CopyableInfoRow
          label="PASSWORD"
          value={wifiInfo.password}
          onCopy={() => handleCopy(wifiInfo.password, 'Password')}
          labelColor={colors.text.subtle}
          valueColor={colors.text.primary}
          iconColor={colors.text.subtle}
        />
      </View>

      <View style={[styles.divider, { backgroundColor: colors.separator }]} />

      <CopyableInfoRow
        label="UNIT"
        value={`Unit ${wifiInfo.unit}`}
        labelColor={colors.text.subtle}
        valueColor={colors.text.primary}
      />
    </GlassCard>
  );
};

const styles = StyleSheet.create({
  card: {
    position: 'relative',
    marginBottom: 16,
    flex: 1,
  },
  qrButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  cbxBadge: {
    alignSelf: 'flex-start',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 4,
    marginBottom: 12,
  },
  subtitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 16,
  },
  subtitle: {
    ...REDESIGN_TYPOGRAPHY.small,
  },
  credentialsSection: {
    marginBottom: 14,
  },
  divider: {
    height: 1,
    marginVertical: 14,
  },
});
